import { useEffect, useRef, useState } from 'react'
import { TESTPHASE_TAGE } from '@raporto/shared'
import { Marke } from './Marke.js'
import { Markenbuehne } from './Markenbuehne.js'

/**
 * Der Abschlussbereich der öffentlichen Seiten.
 *
 * Er ist keine Linkliste am Seitenende, sondern die letzte Fläche, die jemand
 * sieht. Deshalb bekommt er die Höhe und das Gewicht einer eigenen Sektion:
 * Tiefenraum, eine Bühne in der Mitte, die Verweise links und rechts davon.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Warum das hier ohne framer-motion und ohne WebGL steht
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Die Vorlage arbeitet mit Federn aus framer-motion und einer Spline-Szene.
 * Beides ist in diesem Projekt nicht installiert, und beides würde zwei
 * Abhängigkeiten für einen Fussbereich bedeuten – framer-motion bringt rund
 * 60 kB, die Spline-Laufzeit ein Vielfaches davon, und zwar auf jeder Seite,
 * die diesen Fuss zeigt.
 *
 * Ersetzt sind sie so:
 *
 *   Die Federn durch zwei CSS-Variablen (--fs-x, --fs-y) und
 *   `transition: transform` mit cubic-bezier(.16,1,.3,1). Das schwingt nicht
 *   nach wie eine echte Feder, folgt dem Zeiger aber mit demselben weichen
 *   Auslauf. Jede Schicht bekommt eine andere Dauer, damit nichts im
 *   Gleichtakt läuft.
 *
 *   Die 3D-Szene durch eine gezeichnete Bühne: Rapport, Offerte, Rechnung als
 *   drei Karten hintereinander, verbunden durch eine Linie. Das ist zugleich
 *   die Aussage des Produkts – aus einem Datensatz werden drei Belege – und
 *   kostet ein SVG statt einer Laufzeitumgebung.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Drei Dinge, die nicht übergangen werden dürfen
 * ────────────────────────────────────────────────────────────────────────────
 *
 *   1. Jede Hintergrundschicht ist `pointer-events: none` und liegt hinter dem
 *      Inhalt. Sonst fängt der Lichtkegel die Klicks auf die Verweise ab – das
 *      ist der Fehler, den man erst bemerkt, wenn ein Kunde schreibt, das
 *      Impressum lasse sich nicht anklicken.
 *
 *   2. Die Zeigerverfolgung läuft erst nach dem Einhängen und nur auf echten
 *      Mauszeigern. Auf einem Handy gibt es keinen Zeiger, und ein Finger, der
 *      über die Fläche fährt, soll die Bühne nicht kippen.
 *
 *   3. Der Zeiger wird über requestAnimationFrame gedrosselt. `pointermove`
 *      feuert bei jedem Bildpunkt; ungedrosselt schreibt man hundertmal pro
 *      Sekunde in den Stil und erzeugt genau das Ruckeln, das die Bewegung
 *      vermeiden soll.
 *
 * Unter `prefers-reduced-motion` steht alles still und die Zeigerverfolgung
 * bleibt aus.
 */

export type FussVerweis = { pfad: string; wort: string }

/* Das Sternenfeld: [links %, oben %, Grösse px]. Von Hand gesetzt, damit es
   nicht nach Raster aussieht – Zufallswerte pro Laden würden bei jedem
   Seitenwechsel ein anderes Bild geben. */
const STERNE: Array<[number, number, number]> = [
  [5, 22, 2], [13, 64, 1], [21, 33, 2], [29, 78, 1], [37, 14, 2], [45, 51, 1],
  [8, 86, 1], [17, 43, 2], [26, 10, 1], [34, 92, 2], [43, 25, 1], [51, 69, 2],
  [57, 17, 1], [63, 43, 2], [70, 75, 1], [77, 29, 2], [82, 58, 1], [87, 21, 2],
  [91, 73, 1], [95, 41, 2], [67, 90, 1], [11, 53, 1], [39, 63, 2], [75, 11, 1]
]

const PRODUKT: FussVerweis[] = [
  { pfad: '/funktionen', wort: 'So funktioniert’s' },
  { pfad: '/branchen', wort: 'Branchen' },
  { pfad: '/preise', wort: 'Pakete' },
  { pfad: '/faq', wort: 'FAQ' }
]

const RECHTLICHES: FussVerweis[] = [
  { pfad: '/datenschutz', wort: 'Datenschutz' },
  { pfad: '/agb', wort: 'AGB' },
  { pfad: '/impressum', wort: 'Impressum' }
]

/*
  Marke und Kontakt stehen unten, nicht bei den Produktverweisen.

  Beides sind Seiten über den Absender, nicht über das Produkt – und beide
  werden von jemandem gesucht, der schon weiss, was Raporto ist, und nun
  wissen will, mit wem er es zu tun hat. In der Produktspalte stünden sie
  zwischen «Pakete» und «FAQ» und würden dort nur stören.
*/
const UEBER_UNS: FussVerweis[] = [
  { pfad: '/marke', wort: 'Marke' },
  { pfad: '/kontakt', wort: 'Kontakt' }
]

/*
  Die Pillen sagen, was vor dem Klick gilt.

  «Zahlungsart erforderlich» steht bewusst dabei und nicht nur das Angenehme.
  Wer es erst im Formular erfährt, bricht dort ab – und ärgert sich, statt
  sich zu entscheiden.
*/
const PILLEN = [
  /*
    Die Zahl kommt aus der Preisliste und stand hier getippt.

    Eine getippte Zahl im Fuss jeder Seite ist eine Zusage an jeden Besucher,
    und sie war die letzte Stelle, die bei einer Änderung der Frist jemandem
    einfällt. Deshalb kommt sie jetzt von dort, wo die Frist entschieden
    wird.
  */
  `${TESTPHASE_TAGE} Tage testen`,
  'Ohne Mindestlaufzeit',
  'Ohne Einrichtungsgebühr',
  'Zahlungsart erforderlich'
]

export function Fuss({ aufPfad }: { aufPfad: (pfad: string) => void }): JSX.Element {
  const wurzel = useRef<HTMLElement>(null)

  /* Erst nach dem Einhängen prüfen – vorher weiss die Seite nicht, womit sie
     bedient wird, und ein Server-Render gäbe eine andere Antwort. */
  const [zeiger, setZeiger] = useState(false)
  useEffect(() => {
    const ruhig = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const fein = window.matchMedia('(hover: hover) and (pointer: fine)').matches
    setZeiger(fein && !ruhig)
  }, [])

  useEffect(() => {
    const el = wurzel.current
    if (!el || !zeiger) return
    let bild = 0
    const beiBewegung = (e: PointerEvent): void => {
      if (bild) return
      bild = requestAnimationFrame(() => {
        bild = 0
        const r = el.getBoundingClientRect()
        const x = (e.clientX - r.left) / r.width - 0.5
        const y = (e.clientY - r.top) / r.height - 0.5
        el.style.setProperty('--fs-x', x.toFixed(4))
        el.style.setProperty('--fs-y', y.toFixed(4))
        el.style.setProperty('--fs-licht-x', `${Math.round(e.clientX - r.left)}px`)
        el.style.setProperty('--fs-licht-y', `${Math.round(e.clientY - r.top)}px`)
        el.style.setProperty('--fs-licht', '1')
      })
    }
    const beiVerlassen = (): void => {
      el.style.setProperty('--fs-x', '0')
      el.style.setProperty('--fs-y', '0')
      el.style.setProperty('--fs-licht', '0')
    }
    el.addEventListener('pointermove', beiBewegung)
    el.addEventListener('pointerleave', beiVerlassen)
    return () => {
      if (bild) cancelAnimationFrame(bild)
      el.removeEventListener('pointermove', beiBewegung)
      el.removeEventListener('pointerleave', beiVerlassen)
    }
  }, [zeiger])

  return (
    <footer className="rp-fs" ref={wurzel} id="kontakt" data-zeiger={zeiger ? 'ja' : 'nein'}>
      {/* Tiefenraum – drei Schichten, alle hinter dem Inhalt und ohne Klickfang. */}
      <div className="rp-fs-raum" aria-hidden="true">
        <div className="rp-fs-aurora" />
        <div className="rp-fs-korn" />
        <div className="rp-fs-sterne">
          {STERNE.map((s, i) => (
            <span
              key={i}
              style={{ left: `${s[0]}%`, top: `${s[1]}%`, width: s[2], height: s[2] }}
            />
          ))}
        </div>
        <div className="rp-fs-auslauf" />
      </div>
      {zeiger && <div className="rp-fs-licht" aria-hidden="true" />}

      <div className="rp-fs-inhalt">
        <p className="rp-fs-augenbraue">Raporto</p>
        <h2 className="rp-fs-titel">
          Administration, <span>die mitarbeitet.</span>
        </h2>

        <div className="rp-fs-gitter">
          <nav className="rp-fs-gruppe rp-fs-links" aria-label="Produkt">
            <p className="rp-fs-gruppentitel">Produkt</p>
            <ul>
              {PRODUKT.map((v) => (
                <li key={v.wort}>
                  <a
                    href={v.pfad}
                    className="rp-fs-verweis"
                    onClick={(e) => {
                      e.preventDefault()
                      aufPfad(v.pfad)
                    }}
                  >
                    {v.wort}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="rp-fs-buehne">
            <Markenbuehne />
          </div>

          <nav className="rp-fs-gruppe rp-fs-rechts" aria-label="Rechtliches">
            <p className="rp-fs-gruppentitel">Rechtliches</p>
            <ul>
              {RECHTLICHES.map((v) => (
                <li key={v.wort}>
                  <a
                    href={v.pfad}
                    className="rp-fs-verweis"
                    onClick={(e) => {
                      e.preventDefault()
                      aufPfad(v.pfad)
                    }}
                  >
                    {v.wort}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <ul className="rp-fs-pillen">
          {PILLEN.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      </div>

      <div className="rp-fs-boden">
        <div className="rp-fs-marke">
          <Marke groesse={26} />
          <span>Administration, die mitarbeitet.</span>
        </div>
        <div className="rp-fs-bodenrechts">
          {UEBER_UNS.map((v) => (
            <a
              key={v.wort}
              href={v.pfad}
              className="rp-fs-verweis"
              onClick={(e) => {
                e.preventDefault()
                aufPfad(v.pfad)
              }}
            >
              {v.wort}
            </a>
          ))}
          <span className="rp-fs-jahr">© 2026 Raporto</span>
          <button
            type="button"
            className="rp-fs-hoch"
            aria-label="Nach oben"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 19V5" />
              <path d="m5 12 7-7 7 7" />
            </svg>
          </button>
        </div>
      </div>
    </footer>
  )
}
